import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar, Clock, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function BlogCard({ article, language }) {
  const title = article.title?.[language] || article.title?.es || '';
  const excerpt = article.excerpt?.[language] || article.excerpt?.es || '';
  const slug = article.slug?.[language] || article.slug?.es || article.id;
  const categoryName = article.category?.name?.[language] || article.category?.name?.es || '';
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Link 
      to={`/blog/${slug}`}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group flex flex-col"
      data-testid="blog-preview-card"
    >
      {article.featured_image ? (
        <div className="aspect-[16/10] overflow-hidden">
          <img 
            src={article.featured_image} 
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="aspect-[16/10] bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center">
          <span className="text-5xl opacity-30">📝</span>
        </div>
      )}
      
      <div className="p-4 flex-1 flex flex-col">
        {categoryName && (
          <span 
            className="self-start px-2 py-0.5 text-xs font-medium rounded-full mb-2"
            style={{ backgroundColor: '#D4A574', color: 'white' }}
          >
            {categoryName}
          </span>
        )}
        
        <h3 className="font-serif text-lg font-bold text-primary mb-2 group-hover:text-gold transition-colors line-clamp-2 flex-1">
          {title}
        </h3>
        
        <p className="text-secondary text-sm mb-3 line-clamp-2">
          {excerpt}
        </p>
        
        <div className="flex items-center justify-between text-xs text-secondary mt-auto">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(article.published_at)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {article.reading_time} min
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogSection() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/blog/articles/latest`, {
          params: { limit: 4 }
        });
        setArticles(res.data || []);
      } catch (error) {
        console.error('Error fetching latest articles:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, []);

  // Don't render section if no articles
  if (!loading && articles.length === 0) {
    return null;
  }

  return (
    <section className="py-12 px-4" data-testid="blog-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="font-serif text-3xl font-bold text-primary">
            {t('home.latestArticles') || 'Ultimi Articoli'}
          </h2>
          <Link 
            to="/blog" 
            className="text-gold flex items-center hover:underline text-sm font-medium" 
            style={{ color: '#D4A574' }}
          >
            {t('common.viewAll') || 'Ver Todos'} 
            <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-[16/10] bg-gray-200" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-16" />
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {articles.map(article => (
              <BlogCard key={article.id} article={article} language={language} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
